import { formatDateOnlyForPicker, normalizeDateOnly } from './dateOnly';

export const INVOICE_DATE_RANGE_PRESETS = [
  { text: 'This Month', value: 'this_month' },
  { text: 'Last Month', value: 'last_month' },
  { text: 'Last 30 Days', value: 'last_30_days' },
  { text: 'Custom Range', value: 'custom' },
];

export const INVOICE_DATE_RANGE_PRESET_LABELS = INVOICE_DATE_RANGE_PRESETS.reduce(
  (labels, option) => {
    labels[option.value] = option.text;
    return labels;
  },
  {},
);

export const EMPTY_INVOICE_DATE_RANGE_FILTER = {
  preset: null,
  startDate: null,
  endDate: null,
};

function toIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

export function resolveInvoiceDateRangePreset(preset, referenceDate = new Date()) {
  if (!preset || preset === 'custom') {
    return { startDate: null, endDate: null };
  }

  const today = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate(),
  );

  if (preset === 'this_month') {
    return {
      startDate: toIsoDate(startOfMonth(today)),
      endDate: toIsoDate(endOfMonth(today)),
    };
  }

  if (preset === 'last_month') {
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return {
      startDate: toIsoDate(startOfMonth(lastMonth)),
      endDate: toIsoDate(endOfMonth(lastMonth)),
    };
  }

  if (preset === 'last_30_days') {
    const start = new Date(today);
    start.setDate(start.getDate() - 29);
    return {
      startDate: toIsoDate(start),
      endDate: toIsoDate(today),
    };
  }

  return { startDate: null, endDate: null };
}

export function normalizeInvoiceDateRangeFilter(value) {
  if (!value || typeof value !== 'object') {
    return { ...EMPTY_INVOICE_DATE_RANGE_FILTER };
  }

  const preset = value.preset || null;
  const startDate = value.startDate ? formatDateOnlyForPicker(value.startDate) || null : null;
  const endDate = value.endDate ? formatDateOnlyForPicker(value.endDate) || null : null;

  return {
    preset,
    startDate: preset === 'custom' ? startDate : null,
    endDate: preset === 'custom' ? endDate : null,
  };
}

export function areInvoiceDateRangeFiltersEqual(a, b) {
  const left = normalizeInvoiceDateRangeFilter(a);
  const right = normalizeInvoiceDateRangeFilter(b);
  return (
    left.preset === right.preset &&
    (left.startDate || null) === (right.startDate || null) &&
    (left.endDate || null) === (right.endDate || null)
  );
}

export function resolveInvoiceDateRangeForApi(range, referenceDate = new Date()) {
  const normalized = normalizeInvoiceDateRangeFilter(range);
  if (!normalized.preset) {
    return { startDate: null, endDate: null };
  }

  if (normalized.preset !== 'custom') {
    return resolveInvoiceDateRangePreset(normalized.preset, referenceDate);
  }

  return {
    startDate: normalizeDateOnly(normalized.startDate),
    endDate: normalizeDateOnly(normalized.endDate),
  };
}

export function hasActiveInvoiceDateRangeFilter(range) {
  return Boolean(normalizeInvoiceDateRangeFilter(range).preset);
}

export function getInvoiceDateRangeFilterLabel(range) {
  const normalized = normalizeInvoiceDateRangeFilter(range);
  if (!normalized.preset) return '';

  if (normalized.preset !== 'custom') {
    return INVOICE_DATE_RANGE_PRESET_LABELS[normalized.preset] || normalized.preset;
  }

  const start = normalized.startDate;
  const end = normalized.endDate;
  if (start && end) return `${start} – ${end}`;
  if (start) return `From ${start}`;
  if (end) return `Through ${end}`;
  return INVOICE_DATE_RANGE_PRESET_LABELS.custom;
}

export function legacyDueDateToRangeFilter(selectedDueDate) {
  const normalized = normalizeDateOnly(selectedDueDate);
  if (!normalized) return { ...EMPTY_INVOICE_DATE_RANGE_FILTER };
  const pickerValue = formatDateOnlyForPicker(normalized);
  return {
    preset: 'custom',
    startDate: pickerValue,
    endDate: pickerValue,
  };
}
